// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter } from "./common";
import { useEscapeKey } from "../components/useEscapeKey";
import { fullTimestamp, formatAuditTime } from "../components/timeFormat";
import { board } from "../api";
import { copyText } from "../components/clipboard";
import { requestWithCsrf } from "../components/serverUpload";

// 수정내역 보드 — 개발자(내부 운영자)끼리 수정내역을 공유·추적하는 Trello형 칸반.
// 단일 고정 보드 · 4컬럼. 카드: 라벨/태그 · 댓글/스레드 · 사진 첨부. system_admin 전용.

// 라벨 팔레트 — 백엔드 ChangeCard::LABELS와 키 동기화.
const LABEL_META = {
  bug:         { label: "버그",   color: "rose"   },
  feature:     { label: "기능",   color: "green"  },
  improvement: { label: "개선",   color: "sky"    },
  urgent:      { label: "긴급",   color: "amber"  },
  refactor:    { label: "리팩터", color: "gray"   },
  docs:        { label: "문서",   color: "orange" },
};
// 라벨 의미에 맞춘 컬럼 색: 수정사항(주의)=rose → 진행중=amber → 완료=sky → 검수완료(최종)=green.
const COLUMN_ACCENT = {
  backlog:     "var(--rose-500, #ef4444)",   // 수정사항
  in_progress: "var(--amber-500, #f59e0b)",  // 진행중
  review:      "var(--sky-500, #0ea5e9)",    // 완료
  done:        "var(--green-500, #22c55e)",  // 검수완료
};

function DevBoard() {
  const [columns, setColumns] = React.useState([]);
  const [cards, setCards] = React.useState([]);
  const [labelFilter, setLabelFilter] = React.useState("all");
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(null);      // 상세 모달 카드
  const [adding, setAdding] = React.useState(null);   // 인라인 추가중인 컬럼 key
  const [draft, setDraft] = React.useState("");
  const [drag, setDrag] = React.useState(null);       // 드래그중 카드 id
  const [hint, setHint] = React.useState(null);       // { col, index } 드롭 위치 표시

  const applyBoard = (d) => { setColumns(d.columns || []); setCards(d.cards || []); };
  const reload = React.useCallback(() => board.load().then(applyBoard).catch(() => {}), []);
  React.useEffect(() => { reload(); }, [reload]);

  // 상세 모달이 열려 있으면 최신 카드 객체로 동기화(사진/라벨 편집 반영).
  React.useEffect(() => {
    if (!open) return;
    const fresh = cards.find(c => c.id === open.id);
    if (fresh && fresh !== open) setOpen(fresh);
  }, [cards]); // eslint-disable-line

  const visible = React.useMemo(() => cards.filter(c => {
    if (labelFilter !== "all" && !(c.labels || []).includes(labelFilter)) return false;
    if (q) {
      const hay = `${c.title} ${c.description || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [cards, labelFilter, q]);

  const cardsIn = (col) => visible.filter(c => c.column_key === col).sort((a, b) => a.position - b.position);

  // ── 인라인 카드 추가 ──────────────────────────────
  const submitAdd = (col) => {
    const title = draft.trim();
    if (!title) { setAdding(null); setDraft(""); return; }
    board.createCard({ title, column_key: col }).then(() => { setDraft(""); setAdding(null); reload(); });
  };

  // ── 드래그앤드롭 ──────────────────────────────────
  const onDrop = (col) => {
    if (drag == null) return;
    const listInCol = cards.filter(c => c.column_key === col).sort((a, b) => a.position - b.position);
    let index = hint && hint.col === col ? hint.index : listInCol.length;
    // 같은 컬럼 내 이동이면 서버는 '드래그 카드를 뺀 배열' 기준 index를 기대 → 보정.
    const from = listInCol.findIndex(c => c.id === drag);
    if (from !== -1 && from < index) index -= 1;
    board.moveCard(drag, col, index).then(applyBoard).catch(() => {});
    setDrag(null); setHint(null);
  };

  const onCardDragOver = (e, col, idx) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    setHint({ col, index: after ? idx + 1 : idx });
  };

  return (
    <div className="app" style={{ height: "100vh" }}>
      <div className="shell">
        <Sidebar role="platform" active="devboard" />
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Topbar title="수정내역 보드" sub="개발자끼리 수정내역을 공유·추적하는 보드 (내부 전용)" />
          <div className="main" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>

            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div className="h1">수정내역 보드</div>
                <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
                  카드를 끌어 컬럼 사이로 옮기세요 · 총 {cards.length}건
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div className="searchbar" style={{ flex: "0 0 200px" }}>
                  {Ic.search}<input className="input" placeholder="제목·내용 검색" value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <select className="select" value={labelFilter} onChange={e => setLabelFilter(e.target.value)} style={{ width: 130 }}>
                  <option value="all">전체 라벨</option>
                  {Object.entries(LABEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length || 4}, 1fr)`, gap: 14, flex: 1, overflow: "hidden" }}>
              {columns.map(col => {
                const list = cardsIn(col.key);
                return (
                  <div key={col.key}
                    onDragOver={e => { e.preventDefault(); if (drag != null && !hint) setHint({ col: col.key, index: list.length }); }}
                    onDrop={() => onDrop(col.key)}
                    style={{ display: "flex", flexDirection: "column", background: "var(--bg-sub, #f7f5f2)", borderRadius: 12, overflow: "hidden", minWidth: 0 }}>
                    <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--border-soft)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: COLUMN_ACCENT[col.key] || "#999" }} />
                        <b style={{ fontSize: 13 }}>{col.label}</b>
                        <span className="badge badge-gray">{list.length}</span>
                      </div>
                    </div>
                    <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", flex: 1 }}>
                      {list.map((c, idx) => (
                        <React.Fragment key={c.id}>
                          {hint && hint.col === col.key && hint.index === idx && <DropLine />}
                          <CardTile
                            card={c}
                            onOpen={() => setOpen(c)}
                            onDragStart={() => setDrag(c.id)}
                            onDragEnd={() => { setDrag(null); setHint(null); }}
                            onDragOver={e => onCardDragOver(e, col.key, idx)}
                            dimmed={drag === c.id}
                          />
                        </React.Fragment>
                      ))}
                      {hint && hint.col === col.key && hint.index >= list.length && <DropLine />}

                      {adding === col.key ? (
                        <div className="card" style={{ padding: 8 }}>
                          <textarea className="input" autoFocus rows={2} value={draft}
                            placeholder="카드 제목" style={{ width: "100%", resize: "vertical" }}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAdd(col.key); } if (e.key === "Escape") { setAdding(null); setDraft(""); } }} />
                          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                            <button className="btn btn-primary btn-sm" onClick={() => submitAdd(col.key)}>추가</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(null); setDraft(""); }}>취소</button>
                          </div>
                        </div>
                      ) : (
                        <button className="btn btn-ghost btn-sm" style={{ justifyContent: "flex-start" }}
                          onClick={() => { setAdding(col.key); setDraft(""); }}>{Ic.plus} 카드 추가</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
          <AppFooter />
        </div>
      </div>

      {open && (
        <CardDetailModal
          card={open}
          columns={columns}
          onClose={() => setOpen(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function DropLine() {
  return <div style={{ height: 2, borderRadius: 2, background: "var(--sky-500, #0ea5e9)", margin: "1px 2px" }} />;
}

// 컬럼 안의 카드 타일.
function CardTile({ card, onOpen, onDragStart, onDragEnd, onDragOver, dimmed }) {
  return (
    <div className="card" draggable
      onClick={onOpen}
      onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}
      style={{ padding: 11, cursor: "pointer", opacity: dimmed ? 0.4 : 1, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
      {(card.labels || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {card.labels.map(l => {
            const m = LABEL_META[l] || { label: l, color: "gray" };
            return <span key={l} className={"badge badge-" + m.color}>{m.label}</span>;
          })}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{card.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        {card.photos?.length > 0 && <span className="muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3 }}>{Ic.upload}{card.photos.length}</span>}
        {card.comments_count > 0 && <span className="muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3 }}>{Ic.chat}{card.comments_count}</span>}
        {card.created_at && <span className="muted" style={{ fontSize: 11, marginLeft: "auto" }} title={fullTimestamp(card.created_at)}>{formatAuditTime(card.created_at)}</span>}
      </div>
    </div>
  );
}

// ── 클립보드 이미지 붙여넣기 ─────────────────────────────────────────
//
// 스크린샷을 캡처해 바로 Ctrl+V 하는 게 이 보드의 주된 사진 첨부 경로다.
// (파일로 저장 → 파일 선택 다이얼로그는 단계가 두 개 더 많다.)
// 백엔드 제약은 BoardImageAttachable와 동일하게 여기서도 먼저 걸러 안내한다.
const PASTE_ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const PASTE_MAX_BYTES = 10 * 1024 * 1024;

// DataTransfer에서 이미지 파일만 추출. 스크린샷 붙여넣기는 items[kind="file"]로
// 들어오고, 파일 탐색기에서 복사한 이미지는 files에 담기는 브라우저도 있다.
function clipboardImageFiles(dt) {
  const picked = [];
  for (const item of Array.from(dt?.items || [])) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (f && f.type.startsWith("image/")) picked.push(f);
  }
  if (picked.length === 0) {
    for (const f of Array.from(dt?.files || [])) {
      if (f.type.startsWith("image/")) picked.push(f);
    }
  }
  return picked;
}

// 허용 타입·용량으로 거른 뒤 이름을 부여한다. 클립보드 파일은 이름이 전부
// "image.png"라 그대로 올리면 갤러리에서 서로 구분되지 않는다.
function normalizePastedImages(files, seq) {
  const ok = [];
  let rejectedType = 0, rejectedSize = 0;
  files.forEach((f, i) => {
    if (!PASTE_ALLOWED.includes(f.type)) { rejectedType++; return; }
    if (f.size > PASTE_MAX_BYTES) { rejectedSize++; return; }
    const ext = f.type === "image/jpeg" ? "jpg" : f.type === "image/webp" ? "webp" : "png";
    ok.push(new File([f], `pasted-${seq}-${i + 1}.${ext}`, { type: f.type }));
  });
  const reason = rejectedSize ? `${rejectedSize}장은 10MB를 넘어 제외했습니다.`
               : rejectedType ? `${rejectedType}장은 지원하지 않는 형식(JPG·PNG·WebP만)이라 제외했습니다.`
               : null;
  return { files: ok, reason };
}

// 아직 전송하지 않은 첨부(댓글 draft)의 미리보기. 붙여넣은 이미지는 파일명만으로는
// 무엇을 붙였는지 알 수 없어 썸네일이 필요하다. objectURL은 언마운트/교체 시 해제.
function PendingThumbs({ files, onRemove }) {
  const urls = React.useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  React.useEffect(() => () => urls.forEach(u => URL.revokeObjectURL(u)), [urls]);
  if (files.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
      {files.map((f, i) => (
        <div key={`${f.name}-${i}`} style={{ position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border-soft)" }}>
          <img src={urls[i]} alt={f.name} style={{ height: 56, display: "block" }} />
          <button className="btn btn-ghost btn-sm" title="첨부 제거" onClick={() => onRemove(i)}
            style={{ position: "absolute", top: 1, right: 1, padding: "0 4px", background: "rgba(255,255,255,0.85)" }}>{Ic.close}</button>
        </div>
      ))}
    </div>
  );
}

// ── 서버 경로 / 파일전송 ────────────────────────────────────────────
//
// 보드에 붙인 스크린샷을 AI(클로드)에게 보여주려면 "서버에 있는 파일의 절대경로"가
// 필요하다. 보드 첨부는 ActiveStorage(확장자 없는 blob key 경로)라 그대로는 쓰기
// 어렵기 때문에, 파일전송 화면과 같은 storage/uploads 로 사본을 만들고 그 경로를
// 복사한다(백엔드 Admin::UploadsController#from_attachment).
// 첨부(ActiveStorage attachment id) → uploads 사본 생성 후 절대경로 반환.
// CSRF 토큰 첨부·만료 재시도·HTML 응답 방어는 requestWithCsrf가 담당한다.
async function exportAttachmentPath(attachmentId) {
  return requestWithCsrf("/dev/upload/from_attachment", { jsonBody: { attachment_id: attachmentId } });
}

// 임의 파일을 서버로 전송하고 절대경로 반환(이미지 외 로그·PDF 등).
async function uploadToServer(file) {
  const fd = new FormData();
  fd.append("file", file);
  return requestWithCsrf("/dev/upload/file", { body: fd });
}

// 붙여넣기 결과 안내(어디에 몇 장 붙었는지 / 왜 제외됐는지).
function PasteNote({ text }) {
  return (
    <div role="status" style={{ fontSize: 11.5, margin: "6px 0", padding: "6px 9px", borderRadius: 8,
      background: "var(--orange-50)", color: "var(--orange-700)" }}>{text}</div>
  );
}

// 카드 상세 — 제목/내용 편집 · 라벨 토글 · 컬럼 이동 · 사진 갤러리 · 댓글 스레드.
function CardDetailModal({ card, columns, onClose, onChanged }) {
  useEscapeKey(onClose, true);
  const [title, setTitle] = React.useState(card.title);
  const [description, setDescription] = React.useState(card.description || "");
  const [labels, setLabels] = React.useState(card.labels || []);
  const [colKey, setColKey] = React.useState(card.column_key);
  const [saving, setSaving] = React.useState(false);
  const [comments, setComments] = React.useState([]);
  const [cbody, setCbody] = React.useState("");
  const [cfiles, setCfiles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const photoInput = React.useRef(null);
  const commentInput = React.useRef(null);
  const serverFileInput = React.useRef(null);

  const loadComments = React.useCallback(() => board.listComments(card.id).then(setComments).catch(() => {}), [card.id]);
  React.useEffect(() => { loadComments(); }, [loadComments]);

  const dirty = title !== card.title || description !== (card.description || "") ||
    JSON.stringify(labels) !== JSON.stringify(card.labels || []) || colKey !== card.column_key;

  const toggleLabel = (l) => setLabels(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l]);

  const save = () => {
    if (!title.trim()) return;
    setSaving(true);
    board.updateCard(card.id, { title: title.trim(), description, labels, column_key: colKey })
      .then(() => { onChanged(); }).finally(() => setSaving(false));
  };

  const remove = () => {
    if (!window.confirm("이 카드를 삭제할까요? 되돌릴 수 없습니다.")) return;
    board.deleteCard(card.id).then(() => { onChanged(); onClose(); });
  };

  const uploadPhotos = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true);
    board.addPhotos(card.id, arr).then(() => onChanged()).finally(() => setBusy(false));
  };

  // 안내 문구(붙여넣기 결과/거절 사유). 잠깐 띄웠다 지운다.
  // at으로 표시 위치를 나눈다 — 카드 사진에 붙였는데 댓글 쪽에 뜨면(또는 반대면)
  // 어디에 붙었는지 오히려 헷갈린다.
  const [pasteMsg, setPasteMsg] = React.useState(null);   // { at: "card"|"comment", text }
  const flash = React.useCallback((at, text) => {
    setPasteMsg({ at, text });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setPasteMsg(null), 4000);
  }, []);
  React.useEffect(() => () => window.clearTimeout(flash._t), [flash]);

  // 모달 어디서든 Ctrl+V → 카드 사진으로 즉시 업로드.
  // 단 댓글 입력창에서의 붙여넣기는 아래 onPasteComment가 먼저 잡아 댓글 첨부로 간다.
  // 이미지가 없는 붙여넣기(텍스트)는 preventDefault하지 않아 그대로 입력된다.
  const onPasteCard = (e) => {
    const imgs = clipboardImageFiles(e.clipboardData);
    if (imgs.length === 0) return;
    e.preventDefault();
    const { files, reason } = normalizePastedImages(imgs, Date.now());
    if (files.length === 0) { flash("card", reason || "붙여넣을 이미지가 없습니다."); return; }
    uploadPhotos(files);
    flash("card", `사진 ${files.length}장을 카드에 첨부했습니다.${reason ? " " + reason : ""}`);
  };

  // 첨부 → 서버 절대경로 복사. AI에게 "이 파일 봐줘"로 넘기기 위한 동선.
  const copyAttachmentPath = async (attachmentId, at) => {
    setBusy(true);
    try {
      const res = await exportAttachmentPath(attachmentId);
      if (!res?.success) { flash(at, res?.error || "경로를 만들지 못했습니다."); return; }
      const ok = await copyText(res.filepath);
      flash(at, ok ? `서버 경로를 복사했습니다 — ${res.filepath}`
                   : `복사 실패. 경로를 직접 복사하세요: ${res.filepath}`);
    } catch (e) {
      flash(at, e.message || "경로 복사에 실패했습니다.");
    } finally { setBusy(false); }
  };

  // 이미지가 아닌 파일(로그·PDF·zip 등)을 서버로 전송하고 경로를 복사한다.
  // 카드 첨부(이미지 전용)와 달리 파일전송 저장소(storage/uploads)로 간다.
  const sendFileToServer = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadToServer(file);
      if (!res?.success) { flash("card", res?.error || "전송에 실패했습니다."); return; }
      const ok = await copyText(res.filepath);
      flash("card", ok ? `서버로 전송하고 경로를 복사했습니다 — ${res.filepath}`
                       : `전송 완료. 경로를 직접 복사하세요: ${res.filepath}`);
    } catch (e) {
      flash("card", e.message || "전송에 실패했습니다.");
    } finally { setBusy(false); }
  };

  // 댓글 입력창에서의 붙여넣기 → 댓글 첨부(대기 목록)에 쌓는다.
  const onPasteComment = (e) => {
    const imgs = clipboardImageFiles(e.clipboardData);
    if (imgs.length === 0) return;      // 텍스트 붙여넣기는 그대로 통과
    e.preventDefault();
    e.stopPropagation();                // 카드 사진 업로드로 이중 처리되지 않게
    const { files, reason } = normalizePastedImages(imgs, Date.now());
    if (files.length === 0) { flash("comment", reason || "붙여넣을 이미지가 없습니다."); return; }
    setCfiles(prev => [...prev, ...files]);
    flash("comment", `사진 ${files.length}장을 댓글에 첨부했습니다.${reason ? " " + reason : ""}`);
  };
  const removePhoto = (pid) => board.removePhoto(card.id, pid).then(() => onChanged());

  const addComment = () => {
    if (!cbody.trim() && cfiles.length === 0) return;
    setBusy(true);
    board.addComment(card.id, cbody, cfiles).then(() => { setCbody(""); setCfiles([]); loadComments(); onChanged(); }).finally(() => setBusy(false));
  };
  const delComment = (id) => board.deleteComment(card.id, id).then(() => { loadComments(); onChanged(); });

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(20,16,12,0.4)", display: "grid", placeItems: "center", zIndex: 50 }}>
      {/* onPaste는 모달 전체에 건다 — 포커스가 어디에 있든(제목·내용·빈 영역) 스크린샷을
          바로 붙여넣을 수 있게. 이미지가 아니면 기본 붙여넣기 동작을 막지 않는다. */}
      <div onClick={e => e.stopPropagation()} onPaste={onPasteCard} className="card"
        style={{ width: 640, maxHeight: "90vh", padding: 0, display: "flex", flexDirection: "column" }}>
        {/* 헤더 */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-soft)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <input className="input" value={title} onChange={e => setTitle(e.target.value)}
            style={{ fontWeight: 700, fontSize: 16, border: "none", flex: 1, background: "transparent" }} />
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* 컬럼 + 라벨 */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="lbl">상태</span>
              <select className="select" value={colKey} onChange={e => setColKey(e.target.value)} style={{ width: 130 }}>
                {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span className="lbl">라벨</span>
              {Object.entries(LABEL_META).map(([k, m]) => (
                <span key={k} onClick={() => toggleLabel(k)}
                  className={"badge badge-" + m.color}
                  style={{ cursor: "pointer", opacity: labels.includes(k) ? 1 : 0.35 }}>{m.label}</span>
              ))}
            </div>
          </div>

          {/* 내용 */}
          <div>
            <div className="lbl" style={{ marginBottom: 6 }}>내용</div>
            <textarea className="input" rows={4} value={description} onChange={e => setDescription(e.target.value)}
              placeholder="무엇을 수정했는지 적어주세요" style={{ width: "100%", resize: "vertical" }} />
          </div>

          {/* 사진 갤러리 */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div className="lbl">사진 ({card.photos?.length || 0}) <span className="muted" style={{ fontWeight: 400 }}>· Ctrl+V 붙여넣기</span></div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => photoInput.current?.click()}>{Ic.upload} 사진 추가</button>
                {/* 이미지가 아닌 파일은 카드 첨부가 아니라 서버(storage/uploads)로 보내고 경로만 받는다. */}
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => serverFileInput.current?.click()}
                  title="로그·PDF 등 파일을 서버로 전송하고 절대경로를 복사합니다">{Ic.upload} 서버로 파일전송</button>
              </div>
              <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                onChange={e => { uploadPhotos(e.target.files); e.target.value = ""; }} />
              <input ref={serverFileInput} type="file" hidden
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; sendFileToServer(f); }} />
            </div>
            {pasteMsg?.at === "card" && <PasteNote text={pasteMsg.text} />}
            {card.photos?.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {card.photos.map(p => (
                  <div key={p.id} style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-soft)" }}>
                    <a href={p.url} target="_blank" rel="noreferrer">
                      <img src={p.url} alt={p.filename} style={{ width: "100%", height: 84, objectFit: "cover", display: "block" }} />
                    </a>
                    <button className="btn btn-ghost btn-sm" title="삭제" onClick={() => removePhoto(p.id)}
                      style={{ position: "absolute", top: 2, right: 2, padding: "2px 6px", background: "rgba(255,255,255,0.85)" }}>{Ic.close}</button>
                    {/* 서버 절대경로 복사 — AI에게 이 이미지를 열어보라고 할 때 쓴다. */}
                    <button className="btn btn-ghost btn-sm" title="서버 경로 복사" disabled={busy}
                      onClick={() => copyAttachmentPath(p.id, "card")}
                      style={{ position: "absolute", bottom: 2, right: 2, padding: "1px 6px", fontSize: 10,
                               background: "rgba(255,255,255,0.9)", display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <span style={{ width: 11, height: 11, display: "inline-block" }}>{Ic.copy}</span>경로
                    </button>
                  </div>
                ))}
              </div>
            ) : <div className="muted" style={{ fontSize: 12 }}>첨부된 사진이 없습니다 · 스크린샷을 <b>Ctrl+V</b>로 바로 붙여넣을 수 있습니다 (JPG·PNG·WebP, 장당 최대 10MB)</div>}
          </div>

          {/* 댓글 스레드 */}
          <div>
            <div className="lbl" style={{ marginBottom: 8 }}>댓글 ({comments.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {comments.map(cm => (
                <div key={cm.id} style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <b style={{ fontSize: 12 }}>{cm.author || "—"}</b>
                      <span className="muted mono" style={{ fontSize: 10.5 }} title={fullTimestamp(cm.created_at)}>{formatAuditTime(cm.created_at)}</span>
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto", padding: "0 6px" }} onClick={() => delComment(cm.id)}>{Ic.close}</button>
                    </div>
                    {cm.body && <div style={{ fontSize: 12.5, marginTop: 2, whiteSpace: "pre-wrap" }}>{cm.body}</div>}
                    {cm.images?.length > 0 && (
                      <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                        {cm.images.map(im => (
                          <div key={im.id} style={{ position: "relative" }}>
                            <a href={im.url} target="_blank" rel="noreferrer">
                              <img src={im.url} alt={im.filename} style={{ height: 64, borderRadius: 6, border: "1px solid var(--border-soft)", display: "block" }} />
                            </a>
                            <button className="btn btn-ghost btn-sm" title="서버 경로 복사" disabled={busy}
                              onClick={() => copyAttachmentPath(im.id, "comment")}
                              style={{ position: "absolute", bottom: 2, right: 2, padding: "1px 5px", fontSize: 10,
                                       background: "rgba(255,255,255,0.9)" }}>경로</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {comments.length === 0 && <div className="muted" style={{ fontSize: 12 }}>첫 댓글을 남겨보세요.</div>}
            </div>

            {/* 댓글 입력 */}
            <div className="card" style={{ padding: 10, marginTop: 10 }}>
              <textarea className="input" rows={2} value={cbody}
                placeholder="댓글 입력 (Enter 전송, Shift+Enter 줄바꿈 · 이미지 Ctrl+V 첨부)"
                style={{ width: "100%", resize: "vertical" }}
                onChange={e => setCbody(e.target.value)}
                onPaste={onPasteComment}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }} />
              {pasteMsg?.at === "comment" && <PasteNote text={pasteMsg.text} />}
              {cfiles.length > 0 && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>첨부 {cfiles.length}장</div>}
              <PendingThumbs files={cfiles} onRemove={(i) => setCfiles(fs => fs.filter((_, k) => k !== i))} />
              <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => commentInput.current?.click()}>{Ic.upload} 사진</button>
                <input ref={commentInput} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                  onChange={e => { setCfiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={addComment}>등록</button>
              </div>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-soft)", display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn btn-ghost btn-sm" style={{ color: "var(--rose-500, #ef4444)" }} onClick={remove}>카드 삭제</button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
          <button className="btn btn-primary btn-md" disabled={!dirty || saving || !title.trim()} onClick={save}>
            {saving ? "저장 중…" : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { DevBoard };
